import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";
import { Sun, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import heroRoof from "@/assets/hero-roof.jpg";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Azienda registrata. Benvenuto!");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const FIELDS = [
    { k: "company_name", label: "Nome azienda", testid: "reg-company-input", placeholder: "Energia Solare SRL" },
    { k: "name", label: "Il tuo nome", testid: "reg-name-input", placeholder: "Mario Rossi" },
    { k: "email", label: "Email", testid: "reg-email-input", type: "email", placeholder: "mario@energia.it" },
    { k: "password", label: "Password", testid: "reg-password-input", type: "password", placeholder: "Minimo 8 caratteri" },
  ];

  return (
    <div className="min-h-screen bg-[#f4f3f0] text-zinc-900 p-3 sm:p-4 flex gap-4">
      {/* Left image panel */}
      <div className="hidden lg:flex relative flex-1 rounded-[28px] overflow-hidden">
        <img src={heroRoof} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/35" />
        <div className="relative z-10 flex flex-col justify-between p-10 text-white w-full">
          <Link to="/" className="flex items-center gap-2.5 w-max">
            <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center text-zinc-900"><Sun size={18} strokeWidth={2.5} /></div>
            <span className="font-semibold text-lg">SolarFlow</span>
          </Link>
          <div>
            <h2 className="text-3xl xl:text-4xl font-semibold leading-[1.15] tracking-tight max-w-md">Il tuo workspace è pronto in due minuti.</h2>
            <ul className="mt-6 space-y-2.5 text-white/90 text-sm">
              {["Catalogo demo già incluso", "Nessuna carta di credito", "Prima simulazione subito"].map((t) => (
                <li key={t} className="flex items-center gap-2.5"><span className="w-5 h-5 rounded-full bg-white/15 border border-white/25 flex items-center justify-center"><Check size={12} /></span>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-[460px] flex items-center justify-center px-2 sm:px-6 py-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center text-white"><Sun size={20} strokeWidth={2.5} /></div>
            <span className="font-semibold text-xl">SolarFlow</span>
          </Link>

          <h1 className="text-3xl font-semibold tracking-tight">Crea il tuo workspace.</h1>
          <p className="text-zinc-500 mt-2 mb-8">Gratis, senza carta di credito.</p>

          <form onSubmit={submit} className="space-y-4">
            {FIELDS.map((f) => (
              <div key={f.k}>
                <label className="text-sm font-medium text-zinc-700">{f.label}</label>
                <input
                  type={f.type || "text"} required minLength={f.k === "password" ? 6 : undefined}
                  value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  placeholder={f.placeholder} data-testid={f.testid}
                  className="w-full mt-1.5 rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm"
                />
              </div>
            ))}
            <button
              type="submit" disabled={busy}
              data-testid="register-submit-btn"
              className="w-full bg-zinc-900 text-white font-semibold rounded-full py-3.5 flex items-center justify-center gap-2 hover:bg-zinc-800 active:scale-[0.99] transition-all disabled:opacity-50"
            >
              {busy ? "Creazione…" : "Crea workspace"} <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            <Link to="/login" className="text-zinc-500 hover:text-zinc-900 transition-colors">Hai già un account? Accedi</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
